import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../Configurations/Urls";
import {
  applyPreferredOrganizationToUserPayload,
  setClientPreferredOrganizationId,
} from "./clientActiveOrganization";
import {
  getClientOrganizations,
  getAttendanceOrganizationsForUser,
  getEmployeeAttendanceOrganizations,
  getInternAttendanceOrganizations,
  normalizeUserSession,
  resolveActiveOrganizationId,
  hasLinkedEmployeeRecord,
  hasLinkedInternRecord,
  isInternAttendanceUser,
  getManagerAttendanceOrganizations,
  shouldShowManagerAttendanceOrganizations,
  ensureOrganizationsList,
} from "./attendanceAuth";

export async function activateOrganizationAndHydrate(
  organizationId,
  applicationUserId
) {
  const orgId = Number(organizationId);

  setClientPreferredOrganizationId(orgId);

  await axios.post(
    `${MAIN_URL}/api/organizations/extra/active`,
    {
      organization_id: orgId,
      application_user_id: Number(applicationUserId),
    },
    { headers: getApiHeaders() }
  );

  const { data } = await axios.get(`${MAIN_URL}/api/auth/token-login`, {
    headers: getApiHeaders(),
  });

  let user = ensureOrganizationsList(
    applyPreferredOrganizationToUserPayload(data?.user)
  );
  const orgList = shouldShowManagerAttendanceOrganizations(user)
    ? getManagerAttendanceOrganizations(user)
    : getClientOrganizations(user);

  return normalizeUserSession(user, orgList.length ? orgList : getClientOrganizations(user));
}

export async function ensureEmployeeAttendanceSession(user) {
  const employeeOrgs = getEmployeeAttendanceOrganizations(user);

  if (!employeeOrgs.length) {
    const error = new Error("NO_EMPLOYEE_ORG");
    error.code = "NO_EMPLOYEE_ORG";
    throw error;
  }

  const activeId = Number(resolveActiveOrganizationId(user));
  const activeIsEmployeeOrg = employeeOrgs.some(
    (org) => Number(org?.organization_id) === activeId
  );

  if (hasLinkedEmployeeRecord(user) && activeIsEmployeeOrg) {
    return normalizeUserSession(user, employeeOrgs);
  }

  const targetOrgId = activeIsEmployeeOrg
    ? activeId
    : Number(employeeOrgs[0]?.organization_id);

  return activateOrganizationAndHydrate(
    targetOrgId,
    user?.application_user_id
  );
}

export async function ensureInternAttendanceSession(user) {
  const internOrgs = getInternAttendanceOrganizations(user);

  if (!internOrgs.length) {
    const error = new Error("NO_INTERN_ORG");
    error.code = "NO_INTERN_ORG";
    throw error;
  }

  const activeId = Number(resolveActiveOrganizationId(user));
  const activeIsInternOrg = internOrgs.some(
    (org) => Number(org?.organization_id) === activeId
  );

  if (hasLinkedInternRecord(user) && activeIsInternOrg) {
    return normalizeUserSession(user, internOrgs);
  }

  const targetOrgId = activeIsInternOrg
    ? activeId
    : Number(internOrgs[0]?.organization_id);

  return activateOrganizationAndHydrate(
    targetOrgId,
    user?.application_user_id
  );
}

export async function ensureManagerAttendanceSession(user) {
  const scopedUser = ensureOrganizationsList(user);
  const scopedOrgs = getManagerAttendanceOrganizations(scopedUser);

  if (!scopedOrgs.length) {
    const error = new Error("NO_SCOPED_ORG");
    error.code = "NO_SCOPED_ORG";
    throw error;
  }

  const activeId = Number(resolveActiveOrganizationId(scopedUser));
  const activeIsScopedOrg = scopedOrgs.some(
    (org) => Number(org?.organization_id) === activeId
  );

  if (activeIsScopedOrg) {
    return normalizeUserSession(scopedUser, scopedOrgs);
  }

  return activateOrganizationAndHydrate(
    Number(scopedOrgs[0]?.organization_id),
    scopedUser?.application_user_id
  );
}

/** Restores attendance session for self or manager mode (setting from API). */
export async function ensureAttendanceSession(user) {
  if (shouldShowManagerAttendanceOrganizations(user)) {
    return ensureManagerAttendanceSession(user);
  }

  if (isInternAttendanceUser(user)) {
    return ensureInternAttendanceSession(user);
  }

  return ensureEmployeeAttendanceSession(user);
}
